import { feature, point } from "@turf/helpers";
import length from "@turf/length";
import lineChunk from "@turf/line-chunk";
import nearestPointOnLine from "@turf/nearest-point-on-line";
import GeoJSON, { Feature, FeatureCollection, LineString } from "geojson";
import { numbersEqualWithin } from "./geojson-helpers";

/** Maximum number of line features an automatic split operation may create. */
export const MAX_AUTOMATIC_LINE_SPLIT_RESULTS = 100;
const LINE_SPLIT_DISTANCE_TOLERANCE_METERS = 1e-3;

/** Turf can emit a tiny final chunk when the line length is an exact multiple of the chunk size. */
function chunkLineString(lineStringGeometry: GeoJSON.LineString, chunkLengthMeters: number) {
    return lineChunk(lineStringGeometry, chunkLengthMeters, { units: "meters" })
        .features.filter((chunk) => length(chunk, { units: "meters" }) > LINE_SPLIT_DISTANCE_TOLERANCE_METERS)
        .map((chunk) => chunk.geometry);
}

/**
 * Splits a line into consecutive chunks of the requested length, retaining a shorter remainder chunk.
 * Returns no chunks when the distance is invalid or the split would exceed the automatic-result limit.
 */
export function splitLineStringByRepeatedDistance(lineStringGeometry: GeoJSON.LineString, distanceMeters: number) {
    const lineLength = length(feature(lineStringGeometry), { units: "meters" });
    if (distanceMeters <= 0 || distanceMeters >= lineLength) {
        return [];
    }

    // Reject the operation rather than truncating split points and producing an oversized final segment.
    if (Math.ceil(lineLength / distanceMeters) > MAX_AUTOMATIC_LINE_SPLIT_RESULTS) {
        return [];
    }

    return chunkLineString(lineStringGeometry, distanceMeters);
}

/**
 * Splits a line into the requested number of equal-length chunks.
 * Returns no chunks when the count is invalid, the line has no length, or the result limit would be exceeded.
 */
export function splitLineStringBySegmentCount(lineStringGeometry: GeoJSON.LineString, segmentCount: number) {
    const lineLength = length(feature(lineStringGeometry), { units: "meters" });
    if (!Number.isInteger(segmentCount) || segmentCount < 2 || segmentCount > MAX_AUTOMATIC_LINE_SPLIT_RESULTS || lineLength <= 0) {
        return [];
    }

    return chunkLineString(lineStringGeometry, lineLength / segmentCount);
}

/**
 * Splits a LineString feature at the given point into two LineString features.
 * If the split point is at the start or end of the line, returns a single feature (the full line).
 * If the split point is not exactly on the line, the nearest point on the line is used.
 */
export function splitLine<TProperties>(line: Feature<LineString, TProperties>, splitPoint: { lng: number; lat: number }): FeatureCollection<LineString, TProperties> {
    const coords = line.geometry.coordinates;

    if (coords.length < 2) {
        return { type: "FeatureCollection", features: [{ ...line, geometry: { ...line.geometry, coordinates: [...coords] } }] };
    }

    // Use nearestPointOnLine to find the exact point on the line and the segment index
    const nearest = nearestPointOnLine(line, point([splitPoint.lng, splitPoint.lat]));
    const nearestCoord = nearest.geometry.coordinates;
    // properties.index is the index of the nearest vertex in turf v6
    const nearestVertexIndex: number = nearest.properties.index;

    // Check if the split point is at the start or end of the line
    if (numbersEqualWithin(nearestCoord, coords[0]) || numbersEqualWithin(nearestCoord, coords[coords.length - 1])) {
        return { type: "FeatureCollection", features: [{ ...line, geometry: { ...line.geometry, coordinates: [...coords] } }] };
    }

    // Split at the nearest point, inserting it into the correct segment
    const segmentIndex = nearestVertexIndex;
    const firstCoords = [...coords.slice(0, segmentIndex + 1), nearestCoord];
    const secondCoords = [nearestCoord, ...coords.slice(segmentIndex + 1)];

    return {
        type: "FeatureCollection",
        features: [
            { ...line, geometry: { type: "LineString", coordinates: firstCoords } },
            { ...line, geometry: { type: "LineString", coordinates: secondCoords } },
        ],
    };
}
